import type {
  Automation,
  AutomationConfig,
  AutomationScope,
} from "@/lib/data/schemas/automations";
import {
  infiniteQueryOptions,
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  listAutomations,
  saveAutomation,
  listAutomationUploaders,
  getAutomation,
  listAutomationFiles,
  listAutomationCapabilities,
  listAutomationRuns,
  listContentTags,
} from "@/lib/data/requests/automations";
export const automationsQueryKey = ["automations"] as const;
export const contentTagsQueryKey = ["content-tags"] as const;
export const automationsQueryOptions = (scope: AutomationScope = "firm") =>
  infiniteQueryOptions({
    queryKey: [...automationsQueryKey, "list", scope],
    queryFn: ({ pageParam }) => listAutomations(scope, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
export const contentTagsQueryOptions = () =>
  infiniteQueryOptions({
    queryKey: contentTagsQueryKey,
    queryFn: ({ pageParam }) => listContentTags(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
export const capabilitiesQueryOptions = () =>
  queryOptions({
    queryKey: ["automation-capabilities"],
    queryFn: listAutomationCapabilities,
    staleTime: 60_000,
  });
export const automationRunsQueryOptions = (id: string) =>
  infiniteQueryOptions({
    queryKey: ["automation-runs", id],
    queryFn: ({ pageParam }) => listAutomationRuns(id, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    refetchInterval: 10_000,
  });

export const automationFilesQueryOptions = (
  query: string,
  includeUnprocessed = false,
) =>
  infiniteQueryOptions({
    queryKey: ["automation-files", query, includeUnprocessed],
    queryFn: ({ pageParam }) =>
      listAutomationFiles(query, pageParam, includeUnprocessed),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

export const automationDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: [...automationsQueryKey, id],
    queryFn: () => getAutomation(id),
    staleTime: 0,
  });

export const automationUploadersQueryOptions = (query: string) =>
  infiniteQueryOptions({
    queryKey: ["automation-uploaders", query],
    queryFn: ({ pageParam }) => listAutomationUploaders(query, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
  });

export const useSaveAutomation = (
  automation: Automation | undefined,
  onSaved: () => void,
) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (config: AutomationConfig) =>
      saveAutomation(config, automation?.id, automation?.currentRevisionId),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: automationsQueryKey });
      onSaved();
    },
  });
};
